import { type FunctionComponent, useCallback, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { FreeMode } from "swiper/modules";
import "swiper/css";

import { useTrackImpressionEventByIntersection } from "shared/hooks";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { seriesWidgetQuery$key } from "scmp-app/queries/__generated__/seriesWidgetQuery.graphql";

import { SeriesWidgetTabContent } from "./series-widget-tab-content";
import {
  Container,
  SCMPSeriesLink,
  StyledSwiper,
  StyledSwiperSlide,
  Tab,
  TabsContainer,
} from "./styles";

type Props = {
  reference: seriesWidgetQuery$key;
};

export const HomeSeriesWidget: FunctionComponent<Props> = ({ reference }) => {
  const data = useFragment(
    graphql`
      fragment seriesWidgetQuery on Query
      @argumentDefinitions(seriesWidgetArticlesQueueName: { type: "String!" }) {
        seriesWidgetArticles: queue(filter: { name: $seriesWidgetArticlesQueueName }) {
          items {
            edges {
              node {
                ... on Article {
                  entityId
                  seriesShortLabel
                  ...seriesWidgetTabContentArticle
                }
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const articles =
    data?.seriesWidgetArticles?.items?.edges?.filter(edge => edge.node.seriesShortLabel) ?? [];
  const [tab, setTab] = useState(articles[0]?.node?.entityId);

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        content_type: "series", // Required GA4 parameter
        page_section: "home",
        page_type: "homepage",
        widget_name: "scmp_series",
      },
      subcategory: "widget",
    }),
    [],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  if (articles.length === 0) {
    return null;
  }

  const selectedArticle = articles.find(({ node }) => node.entityId === tab)?.node;

  if (!selectedArticle) {
    return null;
  }

  return (
    <Container ref={captureTrackImpressionEventTargetElement}>
      <SCMPSeriesLink as="/series" pathname="/series">
        SCMP Series
      </SCMPSeriesLink>
      <TabsContainer>
        <StyledSwiper freeMode={true} modules={[FreeMode]} slidesPerView="auto" spaceBetween={0}>
          {articles.map(({ node }) => (
            <StyledSwiperSlide key={node.entityId}>
              <Tab $isActive={tab === node.entityId} onClick={() => setTab(node.entityId)}>
                {node.seriesShortLabel}
              </Tab>
            </StyledSwiperSlide>
          ))}
        </StyledSwiper>
      </TabsContainer>
      <SeriesWidgetTabContent reference={selectedArticle} />
    </Container>
  );
};

HomeSeriesWidget.displayName = "HomeSeriesWidget";
